import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wind, Wrench, LockKeyhole, HardHat } from "lucide-react";
import { Logo } from "../components/Logo";
import { IMAGES, BSG_LOGO_URL, BSG_SITE_URL } from "../lib/brand";
import { TestimonialWall } from "../components/TestimonialWall";
import { FloatingContact } from "../components/FloatingContact";

const ChoiceCard = ({ to, label, sub, accent, icon: Icon, image, testid, available = true }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative overflow-hidden border border-white/15 rounded-md bg-[#1a2125] transition-card hover:border-[#f28a16] hover:-translate-y-1 shadow-2xl"
  >
    <div className="absolute inset-0">
      <img
        src={image}
        alt=""
        className="h-full w-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d1214] via-[#0d1214]/85 to-[#0d1214]/35" />
    </div>

    <div className="relative p-8 sm:p-10 lg:p-12 min-h-[340px] flex flex-col justify-between">
      <div className="flex items-start justify-between">
        <div
          className="h-14 w-14 rounded-md flex items-center justify-center shadow-lg"
          style={{ background: accent }}
        >
          <Icon size={26} className="text-black" />
        </div>
        {!available && (
          <span className="font-display uppercase tracking-widest text-xs bg-[#ffc438] text-black px-3 py-1 rounded-sm">
            Coming Soon
          </span>
        )}
      </div>

      <div>
        <div className="font-display uppercase tracking-widest text-xs text-[#ffc438] mb-3">
          {sub}
        </div>
        <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95] text-white">
          {label}
        </h2>
        <div className="mt-6 inline-flex items-center gap-3 font-display uppercase tracking-widest text-sm text-white/90 group-hover:text-[#ffc438] transition">
          <span>{available ? "Enter" : "Preview"}</span>
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  </Link>
);

export const LandingPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-[#f5f6f6]" data-testid="landing-page">
      {/* Top bar */}
      <header className="border-b border-[#cfd7db] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-4 sm:py-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-6 min-w-0 flex-wrap">
            <Logo variant="group" size="lg" />
            {/* Partnership badge — Proud Distributor of BSG Auto Glass */}
            <a
              href={BSG_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bsg-partner-badge"
              className="flex items-center gap-2 sm:gap-3 sm:pl-4 md:pl-6 sm:border-l border-[#cfd7db] hover:opacity-80 transition"
              title="Visit BSG Auto Glass"
            >
              <div className="hidden sm:flex flex-col leading-tight text-right">
                <span className="font-display uppercase tracking-widest text-[10px] text-[#3f4b52]">
                  Proud Distributor of
                </span>
                <span className="font-display font-bold text-sm text-[#111619]">
                  BSG Auto Glass
                </span>
              </div>
              <img
                src={BSG_LOGO_URL}
                alt="BSG Auto Glass"
                className="h-8 sm:h-12 w-auto object-contain"
              />
            </a>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/admin"
              data-testid="landing-admin-login"
              className="inline-flex items-center gap-2 bg-[#20282c] hover:bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs sm:text-sm px-3 sm:px-5 h-10 sm:h-11 rounded-md transition shadow"
            >
              <LockKeyhole size={14} />
              <span className="hidden sm:inline">Admin Login</span>
              <span className="sm:hidden">Admin</span>
            </Link>
            <Link
              to="/team"
              data-testid="landing-team-login"
              className="inline-flex items-center gap-2 bg-[#f28a16] hover:bg-[#20282c] text-white font-display uppercase tracking-widest text-xs sm:text-sm px-3 sm:px-5 h-10 sm:h-11 rounded-md transition shadow"
            >
              <HardHat size={14} />
              <span className="hidden sm:inline">Team Login</span>
              <span className="sm:hidden">Team</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 relative hero-gradient">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 sm:pt-24 pb-20">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 border border-white/20 bg-black/30 px-3 py-1 rounded-sm mb-6 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ffc438] animate-pulse" />
              <span className="font-display uppercase tracking-widest text-[11px] text-white/90">
                Mobile Service · South Africa
              </span>
            </div>
            <h1 className="font-display font-black uppercase leading-[0.9] text-4xl sm:text-5xl lg:text-7xl text-white drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]">
              Choose your <span className="text-[#ffc438]">service.</span>
              <br />
              We bring the <span className="text-[#ffc438]">workshop</span> to you.
            </h1>
            <p className="mt-6 text-white/85 max-w-xl text-base sm:text-lg leading-relaxed">
              Two specialised divisions under one rugged banner. Pick the one
              you need — we'll handle the rest.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 fade-up fade-up-delay-2">
            <ChoiceCard
              to="/windscreens"
              label="Windscreens"
              sub="Division 01 · Glass & Repairs"
              accent="#f28a16"
              icon={Wind}
              image={IMAGES.windscreenDamage}
              testid="landing-windscreens-card"
            />
            <ChoiceCard
              to="/breakdown-assist"
              label="Mobile Breakdown Assist"
              sub="Division 02 · Roadside & Mechanical"
              accent="#E5E5E5"
              icon={Wrench}
              image={IMAGES.mechanic}
              testid="landing-breakdown-card"
              available={false}
            />
          </div>
        </div>
      </main>

      {/* Stats strip on light bg */}
      <section className="bg-white border-y border-[#cfd7db]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6 fade-up fade-up-delay-3">
          <Stat label="Provinces covered" value="6" />
          <Stat label="Glass standard" value="SABS" />
          <Stat label="Insurance" value="Approved" />
          <Stat label="Response" value="Mobile" />
        </div>
      </section>

      <TestimonialWall />

      <footer className="border-t border-[#cfd7db] py-6 text-center text-xs text-[#3f4b52] bg-[#edf2f4]">
        © {new Date().getFullYear()} Diesel & Dust Group ·{" "}
        <Link
          to="/terms"
          className="text-[#f28a16] hover:underline font-semibold"
          data-testid="landing-terms-link"
        >
          Terms &amp; Conditions
        </Link>{" "}
        apply
      </footer>
      <FloatingContact />
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="border-l-2 border-[#f28a16] pl-4">
    <div className="font-display font-black text-3xl sm:text-4xl text-[#111619]">{value}</div>
    <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mt-1">
      {label}
    </div>
  </div>
);

export default LandingPage;
